import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { FinanceFiguresInput } from "@/models/underwriting";

interface ChangeFinanceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial: FinanceFiguresInput;
  onConfirm: (figures: FinanceFiguresInput) => void;
}

const FIELDS: { key: keyof FinanceFiguresInput; label: string; prefix?: string; suffix?: string }[] = [
  { key: "cashPrice", label: "Cash Price", prefix: "£" },
  { key: "deposit", label: "Deposit", prefix: "£" },
  { key: "partExchange", label: "Part Ex Value", prefix: "£" },
  { key: "settlement", label: "Part Ex Settlement", prefix: "£" },
  { key: "loanAmount", label: "Loan Amount", prefix: "£" },
  { key: "term", label: "Term", suffix: "months" },
  { key: "annualMileage", label: "Annual Mileage", suffix: "mi/yr" },
];

export function ChangeFinanceModal({ open, onOpenChange, initial, onConfirm }: ChangeFinanceModalProps) {
  const [values, setValues] = useState<FinanceFiguresInput>(initial);

  // Re-seed the form whenever it is (re)opened with fresh figures.
  useEffect(() => {
    if (open) setValues(initial);
  }, [open, initial]);

  const setField = (key: keyof FinanceFiguresInput, raw: string) => {
    const next = raw === "" ? 0 : Number(raw);
    if (Number.isNaN(next)) return;
    setValues((prev) => ({ ...prev, [key]: next }));
  };

  const handleConfirm = () => {
    onConfirm(values);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change Finance Figures</DialogTitle>
          <DialogDescription>Update the finance figures for this application.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {FIELDS.map(({ key, label, prefix, suffix }) => (
            <div key={key} className="grid grid-cols-[1fr_auto] items-center gap-3">
              <Label htmlFor={`finance-${key}`} className="text-sm text-muted-foreground">
                {label}
              </Label>
              <div className="relative w-40">
                {prefix && (
                  <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    {prefix}
                  </span>
                )}
                <Input
                  id={`finance-${key}`}
                  type="number"
                  min={0}
                  className={cn("text-right", prefix && "pl-6", suffix && "pr-14")}
                  value={String(values[key])}
                  onChange={(e) => setField(key, e.target.value)}
                />
                {suffix && (
                  <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground">
                    {suffix}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-3 sm:gap-3">
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button className="bg-purple-600 text-white hover:bg-purple-700" onClick={handleConfirm}>
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
